import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useLocation } from "wouter"
import cx from "clsx"
import { Button } from "../../../components/Button"
import { ButtonLink } from "../../../components/ButtonLink"
import { fetchLocations, setLocation, enrolDevice } from "../api"
import {
  ApiResponseError,
  isExpectedApiError,
  isNotFound,
  isUnauthorized,
} from "@rawa/api-client/ApiResponseError"
import { postLogout } from "../../auth/api"
import { kioskDeviceQueryKey, useKioskDevice } from "../hooks/useKioskDevice"
import { useKioskIdleTimeout } from "../hooks/useKioskIdleTimeout"
import { listNames, sameIds } from "../utils/locationChoice"
import { trackError } from "@rawa/telemetry"
import { MultiSelectField } from "@rawa/ui/MultiSelectField"
import { paths } from "../../../paths"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./LocationSettingsPage.module.scss"

export function LocationSettingsPage() {
  const [, navigate] = useLocation()
  const queryClient = useQueryClient()

  // Failures of either read are reported by the query client under these
  // sources; a 401 on the device is an answer — no credential yet — not one.
  const locationsQuery = useQuery({
    queryKey: ["kiosk-locations"],
    queryFn: fetchLocations,
    meta: { source: "fetchLocations" },
  })
  const deviceQuery = useKioskDevice("fetchKioskDevice")
  const locations = locationsQuery.data ?? []
  const loading = locationsQuery.isPending

  // A 401 means no device credential, so saving must enrol rather than move
  // (ADR-025). It outranks an earlier answer: a rejected relocation re-asks,
  // and the tablet the server no longer knows is unenrolled from then on.
  // Other failures stay non-fatal while an answer is held; with none, null
  // means the page cannot pick an endpoint, and handleSave must not guess.
  const enrolled = isUnauthorized(deviceQuery.error)
    ? false
    : deviceQuery.data
      ? true
      : null
  const currentLocationIds = enrolled ? deviceQuery.data!.locationIds : []

  // What the device is set to now, and what staff have chosen on this page. A
  // tablet can serve several locations (Relationships-Australia-WA/Services-Delivery-Technology-Upgrade#451), so choosing doesn't
  // save on its own: staff pick every location, then save. Their choice is held
  // apart from the device's, and only once they make one, so the device being
  // asked again — the Wi-Fi rejoining — can never put back what they changed.
  const [chosenIds, setChosenIds] = useState<string[] | null>(null)
  const selectedIds = chosenIds ?? currentLocationIds

  const [saveError, setSaveError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (ids: string[]) =>
      (enrolled === false ? enrolDevice : setLocation)(ids),
  })
  const saving = save.isPending

  const error =
    saveError ??
    (locationsQuery.isError && !locationsQuery.data
      ? "Failed to load locations. Please try again."
      : null) ??
    // Not knowing means we cannot tell enrolment from relocation, so the field
    // stays disabled — say why rather than looking broken.
    (enrolled === null && deviceQuery.isError
      ? "Could not check this device's setup. Please try again."
      : null)

  // enrolled === null means the credential check has not landed, and handleSave
  // cannot pick an endpoint without it.
  const disabled = locations.length < 1 || enrolled === null
  // An enrolled tablet saving what it already has would only sign staff out.
  // An unenrolled one always has something to save: it has no locations yet.
  const unchanged =
    enrolled === true && sameIds(selectedIds, currentLocationIds)
  const canSave = !disabled && !saving && selectedIds.length > 0 && !unchanged

  // Setting the location needs a staff session; clear it whenever they leave
  // this page — idle here, or via "Back" below — so it can't linger on the
  // shared kiosk device. (Saving a location already ends the session
  // server-side.) Logout is best-effort; navigation still happens regardless.
  useKioskIdleTimeout(60_000, () => postLogout())

  // The names to show in the "currently set to …" line — resolved against the
  // loaded list. Empty (message hidden) until both requests land; a current id
  // that isn't among the active locations is left out.
  const currentLocationNames = currentLocationIds.flatMap((id) => {
    const location = locations.find((candidate) => candidate.id === id)
    return location ? [location.name] : []
  })

  async function handleSave() {
    try {
      await save.mutateAsync(selectedIds)
    } catch (err) {
      // A 404 means the server has no usable device for this credential — unknown
      // or revoked — so this page's idea of being enrolled was wrong. Re-ask
      // rather than instructing the staff member: the answer comes back
      // unenrolled, and the next choice enrols instead of relocating.
      if (isNotFound(err)) {
        await deviceQuery.refetch()
        setSaveError(
          "This device needs setting up. Choose its locations and save to continue.",
        )
        return
      }
      if (!isExpectedApiError(err))
        trackError(err, { source: "setKioskLocation" })
      setSaveError(
        err instanceof ApiResponseError
          ? err.message
          : "Could not save the locations. Please try again.",
      )
      return
    }
    // What the guard and the login page know about this tablet is now out of
    // date. Not awaited: the welcome screen's guard asks again regardless.
    void queryClient.invalidateQueries({ queryKey: kioskDeviceQueryKey })
    navigate(paths.kioskWelcome)
  }

  return (
    <>
      <div className={styles.container}>
        <h1 className={cx(typography.h2, styles.heading)}>
          Select your location below
        </h1>
        <div className={styles.card}>
          {currentLocationNames.length > 0 && (
            <>
              <div className={styles.message}>
                <p className={cx(typography.h6, styles.currentLine)}>
                  This device is currently set to the{" "}
                  <span className={styles.currentName}>
                    {listNames(currentLocationNames)}
                  </span>{" "}
                  {currentLocationNames.length === 1 ? "location" : "locations"}
                  .
                </p>
                <p className={styles.helper}>
                  Choose every location this device serves and save, or return
                  to the home screen.
                </p>
              </div>
              <hr className={styles.divider} />
            </>
          )}
          {loading ? (
            <div
              role="status"
              aria-label="Loading locations"
              className={styles.field}
            >
              <span className={styles.skeletonLabel} />
              <span className={styles.skeletonTrigger} />
            </div>
          ) : (
            <div className={styles.field}>
              <div className={styles.locationField}>
                <MultiSelectField
                  label="Locations"
                  options={locations.map((location) => ({
                    id: location.id,
                    name: location.name,
                  }))}
                  selectedIds={selectedIds}
                  onChange={setChosenIds}
                  placeholder="Search locations"
                  required
                  disabled={disabled || saving}
                />
              </div>
              {error && (
                <p role="alert" className={styles.error}>
                  {error}
                </p>
              )}
              <div className={styles.save}>
                <Button onClick={() => void handleSave()} disabled={!canSave}>
                  {saving ? "Saving…" : "Save"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* An unenrolled device has no home screen to return to: the welcome route
          bounces it to the login page, so name that destination honestly. */}
      <ButtonLink
        href={paths.kioskWelcome}
        className={styles.btnReturn}
        onClick={() => void postLogout()}
      >
        {enrolled === false ? "Cancel setup" : "Back to home screen"}
      </ButtonLink>
    </>
  )
}
